import { FC } from "react";
import { Form, Grid } from "antd";
import { FieldContext, PASSENGER_DATA_MAP } from "src/Modules/Flights/constants";
import {  resolveComponent } from "./utils";
import classNames from "classnames";
import { useJourneyStaticDetails } from "../../../../context";
import dayjs from "dayjs";
import { PASSENGER_SECTIONS } from "src/Modules/Flights/constants/JourneyDetails";
import { MOBILE_BREAKPOINT_KEY } from "src/constants";

export const RenderField: FC<any> = ({ fieldName, fieldConfig, context }) => {
  const rules = resolveComponent(fieldConfig.rules, context);
  const initialValue = resolveComponent(fieldConfig.initialValue, context);
  const Component = resolveComponent(fieldConfig.component, context);
  if (Component === null) return null;
  return (
    <Form.Item
      name={[context.index, "basicData", fieldName]}
      label={fieldConfig.label}
      layout={fieldConfig.layout}
      colon={false}
      initialValue={initialValue}
      rules={rules}
      dependencies={fieldConfig.dependencies}
      valuePropName={fieldConfig.valuePropName}
      className={classNames("m-0! font-semibold", fieldConfig?.className)}
    >
      {Component}
    </Form.Item>
  );
};

function usePassengerContext(field: any, passengerType: "Adult" | "Child" | "Infant", form: any): FieldContext {
  const { searchObject, journeySearchData } = useJourneyStaticDetails();
  const isLeadPax = Form.useWatch(["passengers", field.name, "basicData", "isLeadPax"], form);
  const currentPassengerFormData = Form.useWatch(["passengers", field.name, "basicData"], form);
  const isMainPassenger = searchObject?.passengers?.adultCount === 1;
  const isLargeScreen = !!Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];
  return {
    index: field.name,
    form,
    passengerType,
    conditionFields: {
      passportExpiry: searchObject.journeyType === 1 ? dayjs(searchObject.preferredDepartureTime) : dayjs(searchObject.preferredReturnTime),
      isLeadPax: !!isLeadPax,
      currentPassengerFormData,
      departureDate: dayjs(searchObject.preferredDepartureTime),
      validatingAirline: journeySearchData?.validatingAirline,
      isMainPassenger,
      isLargeScreen,
    },
  };
}

export const PassengerHeaderFields: FC<{
  passengerType: "Adult" | "Child" | "Infant";
  field: any;
  form: any;
}> = ({
  field,
  passengerType,
  form,
}) => {
  const allFields = PASSENGER_DATA_MAP[passengerType] || [];
  const headerFields = allFields.filter((col: any) => col.placement === "header");
  const context = usePassengerContext(field, passengerType, form);

  return (
    <>
      {headerFields.map((col: any) => (
        <RenderField key={col.name} fieldName={col.name} fieldConfig={col} context={context} />
      ))}
    </>
  );
};

const PassengerFields: FC<{
  passengerType: "Adult" | "Child" | "Infant";
  field: any;
  form: any;
}> = ({
  field,
  passengerType,
  form,
}) => {
  const allFields = PASSENGER_DATA_MAP[passengerType] || [];
  const bodyFields = allFields.filter((col: any) => col.placement !== "header");
  const context = usePassengerContext(field, passengerType, form);
  

  return (
    <div className="@container flex flex-col gap-4">
      {Object.entries(PASSENGER_SECTIONS).map(([sectionKey, sectionValue]) => {
        const sectionFields = bodyFields
          .sort((a, b) => (a?.order ?? 0) - (b.order ?? 0))
          .filter((filters) => (sectionValue.inputTypes?.[passengerType] as string[]).includes(filters?.name));

        if (sectionFields.length === 0) return null;

        return (
          <div key={sectionKey} className={classNames("@container bg-background-default flex flex-col gap-4 rounded-sm p-4 shadow-sm", sectionValue.className)}>
            <div>
              <div className="text-font-default text-lg font-semibold">{sectionValue.title(context.conditionFields)}</div>
              <div className="text-font-subtle text-sm font-semibold">{sectionValue.subtitle(context.conditionFields)}</div>
            </div>
            <div className="@sm:grid-cols-2 @md:grid-cols-2 grid grid-cols-1 gap-3">
              {sectionFields.map((col: any) => (
                <div key={col.name}>
                  <RenderField fieldName={col.name} fieldConfig={col} context={context} />
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PassengerFields;
